import React from 'react';
import { getInitials, getAvatarColor } from '../utils/avatar';

/**
 * Module-level set of URLs that have already failed to load.
 *
 * When the same broken avatar URL is used by N components simultaneously
 * (e.g. one creator avatar shown in every episode card), each instance would
 * independently fire a network request before any onError can set local state.
 * Sharing this set lets every Avatar after the first failure skip the request
 * entirely, eliminating the "same jpg requested N times" Network pattern.
 */
const _failedUrls = new Set();

/**
 * Avatar component - generates consistent avatars without external dependencies
 */
export function Avatar({
  name,
  src,
  size = 40,
  className = '',
  ...props
}) {
  // Initialise already-errored if the URL has previously failed in this session,
  // so we never render an <img> for a known-broken URL.
  const [imageError, setImageError] = React.useState(
    () => !!src && _failedUrls.has(src)
  );

  const initials = getInitials(name);
  const colors = getAvatarColor(name);

  // Check if src is valid (base64, http URL, or path)
  const isBase64 = src && src.startsWith('data:image');
  const isValidUrl = src && (src.startsWith('http') || src.startsWith('/'));
  const showImage = (isBase64 || isValidUrl) && !imageError;

  if (showImage) {
    return (
      <img
        src={src}
        alt={name || 'Avatar'}
        className={`rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
        onError={() => {
          if (!imageError) {
            _failedUrls.add(src); // mark for all future Avatar instances
            setImageError(true);
          }
        }}
        {...props}
      />
    );
  }
  
  // Fallback to initials avatar
  return (
    <div
      className={`rounded-full flex items-center justify-center font-semibold ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: colors.bg,
        color: colors.text,
        fontSize: size * 0.4,
      }}
      title={name}
      {...props}
    >
      {initials}
    </div>
  );
}

export default Avatar;
